/**
 * Frame3_2_1 - Detailed Assets List
 *
 * Purpose: Detailed listing of all assets from estate plan
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export default function Frame3_2_1() {
  const navigate = useNavigate();
  const { estateId } = useParams();
  const { loadPlannerData } = useSettlementSession(estateId || null);
  const [assets, setAssets] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!estateId) {
        setLoading(false);
        return;
      }

      try {
        const financialAssets = await loadPlannerData('frame-186');
        const realEstate = await loadPlannerData('frame-192');
        const vehicles = await loadPlannerData('frame-193');
        const personalProperty = await loadPlannerData('frame-194');

        setAssets({
          financial: financialAssets,
          realEstate,
          vehicles,
          personalProperty,
        });
      } catch (error) {
        console.error('Failed to load assets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estateId, loadPlannerData]);

  const handleNext = () => {
    navigate(estateId ? `/frame-3-3/${estateId}` : '/frame-3-3');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-3-2/${estateId}` : '/frame-3-2');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading asset details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Detailed Asset List</h1>
          <p className="text-gray-600">
            Complete inventory of assets documented in the estate plan
          </p>
        </div>

        <div className="space-y-6 mb-8">
          {/* Financial Assets */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Assets</h2>
            {assets.financial && Object.keys(assets.financial).length > 0 ? (
              <div className="text-sm text-gray-600">
                <p>Financial accounts and investments documented in estate plan</p>
                <p className="mt-2 text-xs text-gray-500">
                  Full details available during inventory phase
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No financial assets documented</p>
            )}
          </div>

          {/* Real Estate */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Real Estate</h2>
            {assets.realEstate && Object.keys(assets.realEstate).length > 0 ? (
              <div className="text-sm text-gray-600">
                <p>Real property documented in estate plan</p>
                <p className="mt-2 text-xs text-gray-500">
                  Full details available during inventory phase
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No real estate documented</p>
            )}
          </div>

          {/* Vehicles */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Vehicles</h2>
            {assets.vehicles && Object.keys(assets.vehicles).length > 0 ? (
              <div className="text-sm text-gray-600">
                <p>Vehicles documented in estate plan</p>
                <p className="mt-2 text-xs text-gray-500">
                  Full details available during inventory phase
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No vehicles documented</p>
            )}
          </div>

          {/* Personal Property */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Property</h2>
            {assets.personalProperty && Object.keys(assets.personalProperty).length > 0 ? (
              <div className="text-sm text-gray-600">
                <p>Personal property and valuables documented in estate plan</p>
                <p className="mt-2 text-xs text-gray-500">
                  Full details available during inventory phase
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No personal property documented</p>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleNext}>
            View Distribution Plan
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
